import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { useLearnerStore } from '../stores/learnerStore';
import { useLearners } from '../hooks/useLearners';
import { useLearner } from '../hooks/useLearner';
import {
  getSettings,
  updateSettings,
  updateChildSettings,
  deleteAccount,
} from '../api/settings';
import { deleteLearner } from '../api/learners';
import { changePassword } from '../api/auth';
import { ALL_CURRICULA } from '../constants/curricula';
import NavTabSelector from '../components/common/NavTabSelector';
import Button from '../components/common/Button';
import useFocusOnMount from '../hooks/useFocusOnMount';
import useAudioPrefs from '../hooks/useAudioPrefs';
import { useAccess } from '../hooks/useAccess';
import { getBuddyPortraitPath } from '../lib/buddyAssets';
import CloudBand from '../components/common/CloudBand';
import styles from './SettingsPage.module.css';

/* ACCOUNT_TABS moved inside the component so t() is available */

/* Mini two-option selector — looks like NavTabSelector but smaller */
function MiniToggle({ options, value, onChange }) {
  return (
    <div className={styles.miniToggle}>
      {options.map((opt) => {
        const isActive = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`${styles.toggleBtn}${isActive ? ` ${styles.toggleBtnActive}` : ''}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function SettingsPage() {
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuthStore();
  const { data: access } = useAccess();
  const { data: learners = [] } = useLearners();
  const { data: activeLearner } = useLearner();
  const setActiveLearnerId = useLearnerStore((s) => s.setActiveLearnerId);
  const { bgmBiome, bgmReader, sfxEnabled, setBgmBiome, setBgmReader, setSFX } = useAudioPrefs();
  const queryClient = useQueryClient();
  const invalidateLearners = () =>
    queryClient.invalidateQueries({ queryKey: ['learners'] });

  const ACCOUNT_TABS = [
    { key: 'account', label: t('billing.accountTab') },
    { key: 'subscription', label: t('billing.subscriptionTab') },
    { key: 'install', label: t('billing.installTab') },
  ];

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'account');
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPinyin, setShowPinyin] = useState(true);
  const [charSize, setCharSize] = useState('medium');
  const [appLanguage, setAppLanguage] = useState(i18n.language || 'en');
  const [savingPinyin, setSavingPinyin] = useState(false);
  const [savingCharSize, setSavingCharSize] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpError, setCpError] = useState(null);
  const [cpLoading, setCpLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getSettings()
      .then((data) => {
        setParentName(data.name || user?.name || '');
        setParentEmail(data.email || user?.email || '');
        setTimezone(
          data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        if (data.app_language) {
          setAppLanguage(data.app_language);
          i18n.changeLanguage(data.app_language);
        }
      })
      .catch(() => {
        setParentName(user?.name || '');
        setParentEmail(user?.email || '');
        setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (activeLearner) {
      setShowPinyin(activeLearner.pinyin_show ?? true);
      setCharSize(activeLearner.char_size || 'medium');
    }
  }, [activeLearner]);

  function handleTabChange(tab) {
    if (tab === 'subscription') {
      navigate('/account/billing');
      return;
    }
    setActiveTab(tab);
  }

  async function handlePinyinChange(val) {
    const newVal = val === 'yes';
    setShowPinyin(newVal);
    const childId = activeLearner?.id;
    if (!childId) return;
    setSavingPinyin(true);
    try {
      await updateChildSettings(childId, { pinyin_show: newVal });
      invalidateLearners();
    } catch {
      setShowPinyin(!newVal);
    } finally {
      setSavingPinyin(false);
    }
  }

  async function handleCharSizeChange(val) {
    setCharSize(val);
    const childId = activeLearner?.id;
    if (!childId) return;
    setSavingCharSize(true);
    try {
      await updateChildSettings(childId, { char_size: val });
      invalidateLearners();
    } catch {
      setCharSize(charSize);
    } finally {
      setSavingCharSize(false);
    }
  }

  function handleEditProfile() {
    setEditName(activeLearner?.name || parentName);
    setEditingProfile(true);
  }

  async function handleSaveProfile() {
    setSavingProfile(true);
    try {
      const childId = activeLearner?.id;
      if (childId) {
        await updateChildSettings(childId, { name: editName });
        invalidateLearners();
      } else {
        await updateSettings({ name: editName, timezone });
        setParentName(editName);
        // GET /users/me returns the parent name; refresh so other surfaces
        // (e.g. dashboard greeting) pick up the new value without reload.
        queryClient.invalidateQueries({ queryKey: ['me'] });
      }
      setEditingProfile(false);
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleRemoveLearner() {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await deleteLearner(confirmRemove.id);
      // If the deleted learner was the selected one, drop the selection so
      // useLearner falls back to the first remaining active learner.
      if (activeLearner?.id === confirmRemove.id) {
        setActiveLearnerId(null);
      }
      await queryClient.invalidateQueries({ queryKey: ['learners'] });
      setConfirmRemove(null);
    } catch (err) {
      alert(err.message || 'Could not remove learner. Please try again.');
    } finally {
      setRemoving(false);
    }
  }

  async function handleChangePassword() {
    if (cpNew !== cpConfirm) {
      setCpError('New passwords do not match.');
      return;
    }
    if (cpNew.length < 8) {
      setCpError('New password must be at least 8 characters.');
      return;
    }
    setCpError(null);
    setCpLoading(true);
    try {
      await changePassword({ currentPassword: cpCurrent, newPassword: cpNew });
      // Reset and close on success
      setShowChangePassword(false);
      setCpCurrent('');
      setCpNew('');
      setCpConfirm('');
    } catch (err) {
      setCpError(err.message || 'Failed to change password. Please check your current password.');
    } finally {
      setCpLoading(false);
    }
  }

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch {
      setLoggingOut(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteText !== 'DELETE') return;
    setDeleting(true);
    setError(null);
    try {
      await deleteAccount();
    } catch (err) {
      setError(err.message || 'Failed to delete account.');
      setDeleting(false);
      return;
    }
    await logout();
    navigate('/login', { replace: true });
  }

  const displayName =
    activeLearner?.name || parentName || user?.name || 'User';

  // Curriculum display name: look up by slug in ALL_CURRICULA and use the i18n
  // key. The DB `name` column is a stale legacy field — fall back to it only if
  // the slug isn't in the constants table (shouldn't happen in production).
  const curriculumDef = activeLearner?.curriculum?.slug
    ? ALL_CURRICULA.find((c) => c.slug === activeLearner.curriculum.slug)
    : null;
  const curriculumDisplayName = curriculumDef
    ? t(curriculumDef.nameKey)
    : activeLearner?.curriculum?.name;

  return (
    <main className={styles.page} ref={mainRef} tabIndex={-1}>
      <CloudBand variant="teal" className={styles.cloudBand} />
      <div className={styles.tealBand} aria-hidden="true" />

      {/* Content */}
      <div className={styles.content}>
        {/* ── Page Header — Back + Title + Tabs in one consistent row ── */}
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderLeft}>
            <Button variant="back" onClick={() => navigate('/')}>{t('common.back')}</Button>
            <h1 className={styles.pageTitle}>{t('billing.myAccount')}</h1>
          </div>
          <div className={styles.tabWrap}>
            <NavTabSelector
              tabs={ACCOUNT_TABS}
              active={activeTab}
              onChange={handleTabChange}
            />
          </div>
        </div>

        {loading && <p className={styles.loadingText} aria-live="polite">{t('common.loading')}</p>}

        {/* Error banner */}
        {error && (
          <div className={styles.errorBanner} role="alert">
            <p className={styles.errorBannerText}>{error}</p>
          </div>
        )}

        {/* Account tab */}
        {!loading && activeTab === 'account' && (
          <div className={styles.accountTab}>

            {/* Trial banner — only for trialing users */}
            {access?.subscription_status === 'trialing' && access?.trial_end && (() => {
              const daysLeft = Math.max(0, Math.ceil(
                (new Date(access.trial_end) - new Date()) / (1000 * 60 * 60 * 24)
              ));
              return (
                <div className={styles.trialBanner}>
                  <div className={styles.trialBannerText}>
                    <p className={styles.trialBannerTitle}>
                      {t('settings.trialEnds', { count: daysLeft })}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/account/billing')}
                    className={styles.btnUpgrade}
                  >
                    {t('dashboard.upgradeNow')}
                  </button>
                </div>
              );
            })()}

            {/* Main two-column area */}
            <div className={styles.mainColumns}>

              {/* LEFT: learner card(s) + add learner */}
              <div className={styles.learnerCol}>

                {/* Active learner profile card */}
                <div className={styles.learnerProfileCard}>
                  <div className={styles.learnerProfileRow}>
                    <div className={styles.learnerProfileAvatar}>
                      <img
                        src={getBuddyPortraitPath(activeLearner?.buddy?.slug)}
                        alt=""
                        className={styles.learnerProfileAvatarImg}
                      />
                    </div>
                    <div className={styles.learnerProfileInfo}>
                      {editingProfile ? (
                        <div className={styles.editProfileForm}>
                          <label htmlFor="edit-name" className={styles.editNameLabel}>{t('auth.name')}</label>
                          <input
                            id="edit-name"
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className={styles.editNameInput}
                          />
                          <div className={styles.editActions}>
                            <button type="button" onClick={handleSaveProfile} disabled={savingProfile} className={styles.btnSave}>
                              {savingProfile ? t('common.saving') : t('common.save')}
                            </button>
                            <button type="button" onClick={() => setEditingProfile(false)} className={styles.btnCancel}>
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className={styles.learnerProfileName}>{activeLearner?.name || parentName || t('common.learner')}</p>
                          <p className={styles.learnerProfileFramework}>
                            {curriculumDisplayName}
                            {activeLearner?.curriculum && (
                              <>
                                {' '}
                                <button
                                  type="button"
                                  onClick={() => navigate('/settings/curriculum')}
                                  aria-label={t('switchCurriculum.changeAriaLabel')}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    marginLeft: 8,
                                    color: 'var(--color-teal)',
                                    fontFamily: 'var(--font-body)',
                                    fontWeight: 600,
                                    fontSize: 'inherit',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                >
                                  {t('switchCurriculum.changeLink')}
                                </button>
                              </>
                            )}
                          </p>
                        </>
                      )}
                    </div>
                    {!editingProfile && (
                      <button type="button" onClick={handleEditProfile} className={styles.btnEdit}>
                        {t('common.edit')}
                      </button>
                    )}
                  </div>

                  {/* Additional learners (shown when multiple exist) */}
                  {(learners || []).filter(l => l.id !== activeLearner?.id).map((child) => {
                    const childId = child.id;
                    return (
                      <button
                        key={childId}
                        type="button"
                        className={styles.extraLearnerRow}
                        onClick={() => setActiveLearnerId(child.id)}
                        aria-label={`Switch to ${child.name}`}
                      >
                        <div className={styles.extraLearnerAvatar}>
                          <img
                            src={getBuddyPortraitPath(child.buddy?.slug)}
                            alt=""
                            className={styles.extraLearnerAvatarImg}
                          />
                        </div>
                        <p className={styles.extraLearnerName}>{child.name}</p>
                        {(learners || []).length > 1 && (
                          <button
                            type="button"
                            className={styles.btnRemoveLearner}
                            onClick={(e) => { e.stopPropagation(); setConfirmRemove({ id: childId, name: child.name }); }}
                            aria-label={t('settings.removeName', { name: child.name })}
                          >
                            {t('settings.removeName', { name: child.name })}
                          </button>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Add Another Learner */}
                <button
                  type="button"
                  onClick={() => navigate('/settings/add-learner', { state: { isAddingChild: true } })}
                  className={styles.btnAddLearner}
                >
                  {t('settings.addLearner')}
                </button>
              </div>

              {/* RIGHT: settings card */}
              <div className={styles.settingsCard}>

                {/* App Language */}
                <div className={styles.settingRow}>
                  <p className={styles.settingLabel}>{t('settings.appLanguage')}</p>
                  <MiniToggle
                    options={[
                      { key: 'en', label: t('settings.en') },
                      { key: 'zh', label: t('settings.zh') },
                    ]}
                    value={appLanguage}
                    onChange={async (val) => {
                      setAppLanguage(val);
                      i18n.changeLanguage(val);
                      try {
                        await updateSettings({ app_language: val });
                        // app_language is on the user record; refresh ['me']
                        // so any other surface reading from it sees the new
                        // value without a reload.
                        queryClient.invalidateQueries({ queryKey: ['me'] });
                      } catch {
                        // revert on failure
                        const prev = val === 'en' ? 'zh' : 'en';
                        setAppLanguage(prev);
                        i18n.changeLanguage(prev);
                      }
                    }}
                  />
                </div>

                {/* Show Pinyin */}
                <div className={styles.settingRow}>
                  <p className={styles.settingLabel}>{t('settings.showPinyin')}</p>
                  <MiniToggle
                    options={[
                      { key: 'yes', label: t('common.yes') },
                      { key: 'no', label: t('common.no') },
                    ]}
                    value={showPinyin ? 'yes' : 'no'}
                    onChange={handlePinyinChange}
                  />
                  {savingPinyin && <p className={styles.savingHint}>{t('common.saving')}</p>}
                </div>

                {/* Storybook Character Size — label above, buttons row below */}
                <div className={styles.settingBlock}>
                  <p className={styles.settingLabel}>{t('settings.characterSize')}</p>
                  <MiniToggle
                    options={[
                      { key: 'small', label: t('settings.small') },
                      { key: 'medium', label: t('settings.medium') },
                      { key: 'large', label: t('settings.large') },
                      { key: 'xl', label: t('settings.xl') },
                    ]}
                    value={charSize}
                    onChange={handleCharSizeChange}
                  />
                  {savingCharSize && <p className={styles.savingHint}>{t('common.saving')}</p>}
                </div>

                {/* Audio (device-level prefs — localStorage only, no API) */}
                <div className={styles.audioSection}>
                  <p className={styles.settingLabel}>{t('settings.audioSection')}</p>

                  <div className={styles.settingRow}>
                    <p className={styles.settingLabel}>{t('settings.mapMusic')}</p>
                    <MiniToggle
                      options={[
                        { key: 'on', label: t('audio.on') },
                        { key: 'off', label: t('audio.off') },
                      ]}
                      value={bgmBiome ? 'on' : 'off'}
                      onChange={(val) => setBgmBiome(val === 'on')}
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <p className={styles.settingLabel}>{t('settings.storyMusic')}</p>
                    <MiniToggle
                      options={[
                        { key: 'on', label: t('audio.on') },
                        { key: 'off', label: t('audio.off') },
                      ]}
                      value={bgmReader ? 'on' : 'off'}
                      onChange={(val) => setBgmReader(val === 'on')}
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <p className={styles.settingLabel}>{t('settings.soundEffects')}</p>
                    <MiniToggle
                      options={[
                        { key: 'on', label: t('audio.on') },
                        { key: 'off', label: t('audio.off') },
                      ]}
                      value={sfxEnabled ? 'on' : 'off'}
                      onChange={(val) => setSFX(val === 'on')}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className={styles.emailRow}>
                  <p className={styles.emailLabel}>{t('auth.email')}</p>
                  <p className={styles.emailValue}>{parentEmail || '\u2014'}</p>
                  <button type="button" onClick={() => {}} className={styles.btnChange}>
                    {t('common.change')}
                  </button>
                </div>

                {/* Change Password */}
                <button type="button" onClick={() => setShowChangePassword(true)} className={styles.btnChangePassword}>
                  {t('settings.changePassword')}
                </button>

                {/* Log Out */}
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className={styles.btnLogout}
                >
                  {loggingOut ? t('settings.loggingOut') : t('settings.logOut')}
                </button>

                {/* Danger Zone — inside settings card per Figma */}
                <div className={styles.dangerSection}>
                  <div className={styles.dangerHeader}>
                    <p className={styles.dangerTitle}>{t('settings.dangerZone')}</p>
                    <p className={styles.dangerDescription}>{t('settings.deleteWarning')}</p>
                  </div>
                  {!showDeleteConfirm ? (
                    <button type="button" onClick={() => setShowDeleteConfirm(true)} className={styles.btnDeleteAccount}>
                      {t('settings.deleteAccount')}
                    </button>
                  ) : (
                    <div
                      className={styles.deleteConfirm}
                      role="alertdialog"
                      aria-label="Confirm account deletion"
                      onKeyDown={(e) => { if (e.key === 'Escape') { setShowDeleteConfirm(false); setDeleteText(''); } }}
                    >
                      <label htmlFor="delete-confirm" className={styles.deletePrompt}>{t('settings.typeDelete')}</label>
                      <input id="delete-confirm" type="text" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} placeholder={t('settings.typeDeletePlaceholder')} className={styles.deleteInput} />
                      <div className={styles.deleteActions}>
                        <button type="button" onClick={handleDeleteAccount} disabled={deleteText !== 'DELETE' || deleting} className={styles.btnDeleteConfirm}>
                          {deleting ? t('settings.deleting') : t('settings.confirmDelete')}
                        </button>
                        <button type="button" onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }} className={styles.btnDeleteCancel}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Change Password Modal */}
            {showChangePassword && (
              <div
                className={styles.confirmOverlay}
                role="dialog"
                aria-modal="true"
                aria-label="Change password"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowChangePassword(false);
                    setCpCurrent('');
                    setCpNew('');
                    setCpConfirm('');
                    setCpError(null);
                  }
                }}
              >
                <div className={styles.changePasswordDialog}>
                  <p className={styles.confirmTitle}>{t('settings.changePassword')}</p>

                  {cpError && (
                    <div className={styles.errorBanner} role="alert">
                      <p className={styles.errorBannerText}>{cpError}</p>
                    </div>
                  )}

                  <div className={styles.editProfileForm}>
                    <label htmlFor="cp-current" className={styles.editNameLabel}>Current Password</label>
                    <input
                      id="cp-current"
                      type="password"
                      value={cpCurrent}
                      onChange={(e) => setCpCurrent(e.target.value)}
                      className={styles.editNameInput}
                      autoComplete="current-password"
                    />
                  </div>

                  <div className={styles.editProfileForm}>
                    <label htmlFor="cp-new" className={styles.editNameLabel}>New Password</label>
                    <input
                      id="cp-new"
                      type="password"
                      value={cpNew}
                      onChange={(e) => setCpNew(e.target.value)}
                      className={styles.editNameInput}
                      autoComplete="new-password"
                      minLength={8}
                    />
                  </div>

                  <div className={styles.editProfileForm}>
                    <label htmlFor="cp-confirm" className={styles.editNameLabel}>Confirm New Password</label>
                    <input
                      id="cp-confirm"
                      type="password"
                      value={cpConfirm}
                      onChange={(e) => setCpConfirm(e.target.value)}
                      className={styles.editNameInput}
                      autoComplete="new-password"
                      minLength={8}
                    />
                  </div>

                  <div className={styles.editActions}>
                    <button
                      type="button"
                      onClick={handleChangePassword}
                      disabled={cpLoading || !cpCurrent || !cpNew || !cpConfirm}
                      className={styles.btnSave}
                    >
                      {cpLoading ? 'Saving…' : 'Change Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowChangePassword(false);
                        setCpCurrent('');
                        setCpNew('');
                        setCpConfirm('');
                        setCpError(null);
                      }}
                      className={styles.btnCancel}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Remove learner confirm dialog — keep as-is */}
            {confirmRemove && (
              <div className={styles.confirmOverlay}>
                <div className={styles.confirmDialog}>
                  <p className={styles.confirmTitle}>{t('settings.removeConfirm', { name: confirmRemove.name })}</p>
                  <p className={styles.confirmText}>
                    {t('settings.removeWarning')}
                  </p>
                  <div className={styles.confirmActions}>
                    <button type="button" className={styles.btnConfirmCancel} onClick={() => setConfirmRemove(null)} disabled={removing}>
                      {t('settings.keepLearner')}
                    </button>
                    <button type="button" className={styles.btnConfirmRemove} onClick={handleRemoveLearner} disabled={removing}>
                      {removing ? t('settings.removing') : t('settings.removeLearner')}
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Install App tab */}
        {!loading && activeTab === 'install' && (
          <div className={styles.installTab}>
            <div className={styles.installCard}>
              <p className={styles.installTitle}>{t('settings.installTitle')}</p>
              <p className={styles.installSubtitle}>
                {t('settings.installDesc')}
              </p>

              {/* iOS instructions */}
              <div className={styles.instructionBlock}>
                <p className={styles.instructionHeading}>{t('settings.iphone')}</p>
                <ol className={styles.instructionList}>
                  <li>{t('settings.openSafari')}</li>
                  <li>
                    {t('settings.tapShare')}{' '}
                    <span className={styles.shareIcon}>&#x2191;</span>
                  </li>
                  <li>{t('settings.tapAddHome')}</li>
                </ol>
              </div>

              {/* Android instructions */}
              <div className={styles.instructionBlock}>
                <p className={styles.instructionHeading}>{t('settings.android')}</p>
                <ol className={styles.instructionList}>
                  <li>{t('settings.openChrome')}</li>
                  <li>{t('settings.tapMenu')}</li>
                  <li>{t('settings.tapAddHomeAndroid')}</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
